import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from "react-redux";
import { Box, GlobalStyles } from "@mui/material";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import styles from "../../Leadgen.styles";
import StyledTextField from "../../../../common/components/styled-components/StyledTextField";
import {
  receiveEntityNotesRequest,
  removeNoteRequest,
  createUpdateNoteRequest,
} from "../../actions/entityNotes";
import { selectLeadgen } from "../../selectors";
import { formatDateAndTime } from "../../../../common/formats/date-fromatter";
import DefaultButton from "../../../../common/components/button/Button";
import type { RootState } from '../../../../reducers';

type NoteAuthorProps = {
  id?: number | null | undefined;
  name?: string | undefined;
  surname?: string | undefined;
}
type  OwnProps = {
  entityId: string | null;
};
type EntityNotesProps = OwnProps & StateProps;

const editorConfig = {
  toolbar: [
    "heading",
    "|",
    "bold",
    "italic",
    "link",
    "bulletedList",
    "numberedList",
    "blockQuote",
  ],
  heading: {
    options: [
      {
        model: "paragraph",
        title: "Paragraph",
        class: "ck-heading_paragraph",
      },
      {
        model: "heading1",
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1",
      },
      {
        model: "heading2",
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2",
      },
    ],
  },
} as const;

const isEditorEmpty = (html: string) => {
  const text = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, "")
    .trim();
  return text?.length === 0;
}

const EntityNotes: React.FC<EntityNotesProps> = ({entityId, entityNotes}) => {
  const dispatch = useDispatch();
  const [note, setNote] = useState(entityNotes?.entityNote?.body ?? "");
  const [title, setTitle] = useState(entityNotes?.entityNote?.title ?? "");
  const [noteId, setNoteId] = useState(null);
  const [disableNoteEdit, setDisableNoteEdit] = useState(true);
  const [disabledButtonSave, setDisabledButtonSave] = useState(true);
  const snapTitleRef = useRef(title);
  const snapNoteRef = useRef(note);
  const editorRef = useRef<any>(null);
  const config = useMemo(() => {
    return JSON.parse(JSON.stringify(editorConfig));
  }, [])

   const [noteAuthor, setNoteAuthor] = useState<NoteAuthorProps>({
    id: null,
    name: "User",
    surname: "",
  });
  const noteAuthorFullName =
    noteAuthor.name && noteAuthor.surname
      ? `${noteAuthor.name} ${noteAuthor.surname}`
      : noteAuthor.name
      ? `${noteAuthor.name}`
      : noteAuthor.surname
      ? `${noteAuthor.surname}`
      : "n/a";
  const lastUpdateDate =
    (entityNotes &&
      entityNotes.entityNote &&
      formatDateAndTime(entityNotes.entityNote.updated_at)) ||
    "";
  const recalculationSave = (nextTitle: string, nextBodyHtml: string) => {
    const canSave = nextTitle.trim().length > 0 && !isEditorEmpty(nextBodyHtml);
    setDisabledButtonSave(!canSave);
  };
  const serverBody = useMemo(() => entityNotes?.entityNote?.body ?? "", [entityNotes?.entityNote?.body]);

  useEffect(() => {
    setNote(serverBody);
    editorRef.current?.setData(serverBody);
  }, [entityNotes?.entityNote?.id, serverBody]);

  useEffect(() => {
    if (!entityId) return;
    dispatch(receiveEntityNotesRequest(entityId));
    setDisableNoteEdit(true);
  }, [dispatch, entityId]);

  useEffect(() => {
    if (entityNotes?.entityNote === null) {
      setNote("");
      setTitle("");
      setNoteId(null);
    }
    entityNotes?.entityNote &&
    entityNotes?.entityNote?.body &&
    setNote(entityNotes.entityNote.body);
  }, [dispatch, entityNotes]);

  useEffect(() => {
    if (entityNotes && entityNotes.entityNote !== null) {
      entityNotes?.entityNote &&
      entityNotes?.entityNote?.title &&
      setTitle(entityNotes.entityNote.title);
      if (entityNotes.entityNote.title === null || entityNotes.entityNote.title === undefined) setTitle('')
    }
  }, [dispatch, entityNotes]);

  useEffect(() => {
      entityNotes?.entityNote &&
      entityNotes?.entityNote?.noteAuthor &&
      setNoteAuthor(entityNotes?.entityNote?.noteAuthor);
  }, [dispatch, entityNotes]);

  useEffect(() => {
    entityNotes &&
      entityNotes.entityNote &&
      entityNotes.entityNote.id &&
      setNoteId(entityNotes.entityNote.id);
  }, [dispatch, entityNotes]);

  useEffect(() => {
    const editor = editorRef.current;
    // if (!editor) return;
    if (!editor?.ui?.view?.editable?.element) return;
    if (disableNoteEdit) {
      editor.enableReadOnlyMode?.('ui-lock');
      editor.ui?.view?.editable?.element?.blur?.();
    } else {
      editor.disableReadOnlyMode?.('ui-lock');
    }
  }, [disableNoteEdit]);

  useEffect(() => {
    return () => {
       editorRef.current = null;
    };
  }, []);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const nextTitle = event.target.value;
    setTitle(nextTitle);
    recalculationSave(nextTitle, note);
  };

  const handleChangeNote = (data: string) => {
    setNote(data);
    recalculationSave(title, data);
  };

  const handleEditNote = () => {
    snapTitleRef.current = title;
    snapNoteRef.current = note;
    setDisableNoteEdit(false);
    if (!!title && !!note) {
       setDisabledButtonSave(false);
    } else {
       setDisabledButtonSave(true);
    }
  };

  const handleSaveNote = () => {
    setDisableNoteEdit(true);
    dispatch(
      createUpdateNoteRequest({
        entity_id: entityId,
        body: note,
        title,
        id: noteId,
      })
    );
  };

  const handleDeleteNote = () => {
    dispatch(removeNoteRequest(noteId));
    setNote("");
    setTitle("");
    setNoteId(null);
  };

  const handleCancel = () => {
    setNote(snapNoteRef.current ??
      entityNotes?.entityNote?.body ?? ""
    );
    setTitle(snapTitleRef.current ??
      entityNotes?.entityNote?.title ?? ""
    );
    setDisableNoteEdit(true);
  };

  return (
    <>
      {noteId ? (
        <Box sx={styles.userInfo}>
          Last modified by {noteAuthorFullName}, {lastUpdateDate}
        </Box>
      ) : (
        <Box sx={styles.userInfo}>No saved notes at this time</Box>
      )}
      <GlobalStyles styles={{
        ".ck-content ol": {
          listStyleType: "decimal !important",
          listStylePosition: "outside",
          marginLeft: "1.5rem",
          paddingLeft: 0,
        },
        ".ck-content ul": {
          listStyleType: "disc !important",
          listStylePosition: "outside",
          marginLeft: "1.5rem",
          paddingLeft: 0,
        },
        ".ck-content li": {
          display: "list-item",
        },
        ".ck-content blockquote": {
          borderLeft: "4px solid #ddd",
          margin: ".5em 0 .5em 1em",
          paddingLeft: "1em",
          fontStyle: "italic",
        },

      }}
      />
      <Box sx={{ margin: "0 0 5px 0" }}>
        <StyledTextField
          size="small"
          fullWidth
          required
          label="Title"
          margin="dense"
          onChange={(event) => handleChange(event)}
          value={title}
          disabled={disableNoteEdit}
        />
      </Box>
      <Box sx={styles.editorArea}>
        <CKEditor
          key={disableNoteEdit ? "view" : "edit"}
          editor={ClassicEditor}
          config={config}
          data={note || ""}
          disabled={disableNoteEdit}
          onReady={(editor) => {
            editorRef.current = editor;
            if (disableNoteEdit) {
              editor.enableReadOnlyMode('ui-lock');
              editor.ui?.view?.editable?.element?.blur?.();
            }
          }}
          onFocus={(_, editor) => {
            if (disableNoteEdit) {
              (editor as any).editing?.view?.focusManager?.blur?.();
            }
          }}
          onChange={(_, editor) => {
            if (disableNoteEdit) return;
            const data = editor.getData() || "";
            handleChangeNote(data);
          }}
        />
      </Box>
      {disableNoteEdit ? (
        <Box sx={styles.buttonHolder}>
          <DefaultButton
            variant="contained"
            // sx={styles.buttonEdit}
            styleType="edit"
            handleClick={handleEditNote}
          >
            Edit
          </DefaultButton>
          <DefaultButton
            variant="contained"
            disabled={!noteId}
            // sx={styles.buttonDelete}
            styleType="deleteleadgen"
            handleClick={handleDeleteNote}
          >
            Delete
          </DefaultButton>
        </Box>
      ) : (
        <Box sx={styles.buttonHolder}>
          <DefaultButton
            variant="contained"
            // sx={styles.buttonEdit}
            styleType="edit"
            handleClick={handleSaveNote}
            disabled={disabledButtonSave}
          >
            Save
          </DefaultButton>
          <DefaultButton
            variant="contained"
            // sx={styles.buttonCansel}
            styleType="cancelleadgen"
            handleClick={handleCancel}
          >
            Cancel
          </DefaultButton>
        </Box>
      )}
    </>
  );
};

function mapStateToProps(state: RootState) {
  const entityNotes = selectLeadgen(state);
  return entityNotes;
}
type StateProps = ReturnType<typeof mapStateToProps>;
export default connect<StateProps, {}, OwnProps, RootState>(mapStateToProps)(EntityNotes);
